import { useState } from 'react';
import { createRoute, redirect, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import { Search, CheckCircle2, AlertTriangle, XCircle, Clock, FileText, Repeat, Volume2, TextCursorInput, Bug, ArrowRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { PlayerButton } from '@/client/components/player-button';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/client/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Spinner } from '@/client/components/ui/spinner';
import { API_BASE_URL } from '@/client/config';
import type { VttAnalysisResult, VttGap, VttHallucinationAnalysis } from '@shared/types';
import { VTT_LONG_LINE_THRESHOLD } from '@shared/types';

const vttUrlSchema = z.string().url('Please enter a valid URL');

type VttAnalyzerSearch = {
  url?: string;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'utilities/vtt-analyzer',
  validateSearch: (search: Record<string, unknown>): VttAnalyzerSearch => ({
    url: typeof search.url === 'string' ? search.url : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: VttAnalyzerComponent,
});

/** Format seconds as HH:MM:SS */
function formatTimestamp(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Format seconds as human-readable duration */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function VttAnalyzerComponent() {
  const { getAccessTokenSilently } = useAuth0();
  const { url: initialUrl } = useSearch({ from: '/utilities/vtt-analyzer' });
  const [url, setUrl] = useState(initialUrl ?? '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VttAnalysisResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  /** Check if input looks like an eventID (clientID_eventSuffix) rather than a URL */
  const isEventID = (input: string) => /^\d+_\S+$/.test(input.trim()) && !input.includes('://');

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setLoading(true);
    setResult(null);

    const input = url.trim();

    try {
      const token = await getAccessTokenSilently();

      // If input looks like an eventID, resolve it to a VTT URL first
      let vttUrl = input;
      if (isEventID(input)) {
        const resolveRes = await fetch(`${API_BASE_URL}/api/vtt/resolve/${encodeURIComponent(input)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resolveRes.ok) {
          const err = await resolveRes.json().catch(() => ({ error: 'No caption found' }));
          setValidationError(err.error || 'No caption VTT found for this event');
          setLoading(false);
          return;
        }
        const { url: resolvedUrl } = await resolveRes.json();
        vttUrl = resolvedUrl;
        setUrl(vttUrl); // Update input to show resolved URL
      } else {
        const validation = vttUrlSchema.safeParse(vttUrl);
        if (!validation.success) {
          setValidationError(validation.error.issues[0].message);
          setLoading(false);
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/vtt/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: vttUrl }),
      });

      const data: VttAnalysisResult = await response.json();
      setResult(data);
    } catch (err) {
      setResult({
        url: input,
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
        totalSegments: 0,
        firstTimestamp: 0,
        lastTimestamp: 0,
        totalDurationSeconds: 0,
        minutesWithNoCoverage: [],
        significantGaps: [],
        largeGaps: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const hallucinationScore = result?.hallucinations?.score ?? 0;
  const hasHallucinations = hallucinationScore > 0;
  const hasIssues = result?.success && (result.largeGaps.length > 0 || result.minutesWithNoCoverage.length > 0 || hasHallucinations);
  const isClean = result?.success && !hasIssues;

  return (
    <Layout>
      <div className="space-y-6">
        <section>
          <h3 className="text-2xl font-semibold mb-2">VTT Caption Analyzer</h3>
          <p className="text-muted-foreground mb-6">
            Analyze WebVTT caption files for gaps, missing segments, and coverage issues.
          </p>

          <form onSubmit={handleAnalyze} className="flex gap-2 w-full mb-6">
            <Input
              type="text"
              placeholder="https://m-download.invintus.com/.../captions.vtt or clientID_eventID"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (validationError) setValidationError(null);
              }}
              disabled={loading}
              className="font-mono text-sm"
            />
            <Button type="submit" disabled={loading || !url.trim()}>
              {loading ? <Spinner className="size-4" /> : <Search className="size-4" />}
              <span className="ml-2">Analyze</span>
            </Button>
          </form>
          {validationError && (
            <p className="text-sm text-red-600 -mt-4">{validationError}</p>
          )}
        </section>

        {/* Error State */}
        {result && !result.success && (
          <Card className="border-red-200 dark:border-red-900">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600">
                <XCircle className="size-5" />
                <span className="font-medium">Analysis Failed</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{result.error}</p>
            </CardContent>
          </Card>
        )}

        {/* Success Results */}
        {result?.success && (
          <>
            {/* Event context bar */}
            {result.eventInfo && (
              <div className="flex items-center rounded-lg border border-blue-500/40 bg-blue-500/10 px-4 py-3">
                <span className="text-base text-muted-foreground mr-2">Event:</span>
                <Link
                  to="/events/$eventId"
                  params={{ eventId: result.eventInfo.eventID }}
                  className="text-base font-semibold text-primary hover:underline inline-flex items-center gap-1"
                >
                  {result.eventInfo.eventTitle || result.eventInfo.eventID}
                  <ArrowRight className="size-4" />
                </Link>
                <div className="ml-auto">
                  <PlayerButton clientID={result.eventInfo.clientID} eventID={(() => {
                    const parts = result.eventInfo!.eventID.split('_');
                    return parts.length >= 2 ? parts.slice(1).join('_') : result.eventInfo!.eventID;
                  })()} />
                </div>
              </div>
            )}

            {/* Summary Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="size-5" />
                    Analysis Summary
                  </CardTitle>
                  {isClean ? (
                    <Badge variant="outline" className="border-green-500 text-green-600">
                      <CheckCircle2 className="size-3 mr-1" /> No Issues
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                      <AlertTriangle className="size-3 mr-1" /> Issues Found
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Segments</span>
                    <p className="font-semibold text-lg">{result.totalSegments.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration</span>
                    <p className="font-semibold text-lg">{formatDuration(result.totalDurationSeconds)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Coverage</span>
                    <p className="font-mono">
                      {formatTimestamp(result.firstTimestamp)} - {formatTimestamp(result.lastTimestamp)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Issues</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {hallucinationScore > 0 && (
                        <Badge variant="destructive" className="bg-purple-600">
                          <Bug className="size-3 mr-1" />
                          Hallucinations ({hallucinationScore})
                        </Badge>
                      )}
                      {result.largeGaps.length > 0 && (
                        <Badge variant="destructive">{result.largeGaps.length} large gaps</Badge>
                      )}
                      {result.minutesWithNoCoverage.length > 0 && (
                        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                          {result.minutesWithNoCoverage.length} min uncovered
                        </Badge>
                      )}
                      {isClean && (
                        <Badge variant="outline" className="border-green-500 text-green-600">
                          All clear
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Tabs */}
            <ReportTabs result={result} />
          </>
        )}
      </div>
    </Layout>
  );
}

function ReportTabs({ result }: { result: VttAnalysisResult }) {
  const markers = result.repetition?.markers;
  const phrases = result.repetition?.topRepeatedPhrases ?? [];
  const runs = result.repetition?.runs ?? [];
  const consecutiveDuplicates = result.repetition?.consecutiveDuplicates ?? 0;
  const longestRun = result.repetition?.longestRun ?? 0;
  const longLines = result.quality?.longLines ?? [];
  const avgChars = result.quality?.avgCharsPerSegment ?? 0;
  const hallucinations = result.hallucinations;
  const hallucinationCount = (hallucinations?.wordRepetitions.length ?? 0) +
    (hallucinations?.duplicateTexts.length ?? 0) +
    (hallucinations?.hallucinationPhrases.length ?? 0);

  return (
    <Tabs defaultValue={hallucinationCount > 0 ? 'hallucinations' : 'markers'}>
      <TabsList>
        <TabsTrigger value="hallucinations">
          <Bug className="size-4" />
          Hallucinations
          <Badge
            variant={hallucinationCount > 0 ? 'destructive' : 'secondary'}
            className={hallucinationCount > 0 ? 'bg-purple-600' : ''}
          >
            {hallucinations?.score ?? 0}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="markers">
          <Volume2 className="size-4" />
          Markers
          <Badge variant="secondary">{markers?.totalMarkers ?? 0}</Badge>
        </TabsTrigger>
        <TabsTrigger value="phrases">
          <FileText className="size-4" />
          Phrases
          <Badge variant="secondary">{phrases.length}</Badge>
        </TabsTrigger>
        <TabsTrigger value="runs">
          <Repeat className="size-4" />
          Runs
          <Badge variant="secondary">{runs.length}</Badge>
        </TabsTrigger>
        <TabsTrigger value="longLines">
          <TextCursorInput className="size-4" />
          Long
          <Badge variant={longLines.length > 0 ? 'outline' : 'secondary'} className={longLines.length > 0 ? 'border-yellow-500 text-yellow-600' : ''}>
            {longLines.length}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="largeGaps">
          <XCircle className="size-4" />
          Large Gaps
          <Badge variant={result.largeGaps.length > 0 ? 'destructive' : 'secondary'}>
            {result.largeGaps.length}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="significantGaps">
          <Clock className="size-4" />
          Gaps
          <Badge variant="secondary">{result.significantGaps.length}</Badge>
        </TabsTrigger>
        <TabsTrigger value="uncovered">
          <AlertTriangle className="size-4" />
          Uncovered
          <Badge variant={result.minutesWithNoCoverage.length > 0 ? 'outline' : 'secondary'} className={result.minutesWithNoCoverage.length > 0 ? 'border-yellow-500 text-yellow-600' : ''}>
            {result.minutesWithNoCoverage.length}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="hallucinations">
        <HallucinationsTab hallucinations={hallucinations} />
      </TabsContent>

      <TabsContent value="markers">
        {!markers || markers.totalMarkers === 0 ? (
          <EmptyTabMessage message="No marker segments found in this VTT." />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Volume2 className="size-5" />
                Marker Segments
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {markers.totalMarkers} marker segment{markers.totalMarkers !== 1 ? 's' : ''} ({markers.markerPercentage.toFixed(1)}% of total)
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(markers.byType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <Badge key={type} variant="outline" className="font-mono">
                      [{type}] × {count}
                    </Badge>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="phrases">
        {phrases.length === 0 ? (
          <EmptyTabMessage message="No repeated phrases (3+ occurrences) found in this VTT." />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="size-5" />
                Top Repeated Phrases
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Non-marker text appearing 3+ times (may be legitimate procedural phrases)
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Count</TableHead>
                      <TableHead>Phrase</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {phrases.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-semibold">{item.count}x</TableCell>
                        <TableCell className="text-sm font-mono">{item.phrase}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="runs">
        {runs.length === 0 ? (
          <EmptyTabMessage message="No consecutive identical segments found in this VTT." />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Repeat className="size-5" />
                Consecutive Identical Segments
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {consecutiveDuplicates} consecutive duplicate pair{consecutiveDuplicates !== 1 ? 's' : ''} found
                {longestRun > 0 && ` (longest run: ${longestRun} segments)`}
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Count</TableHead>
                      <TableHead>Time Range</TableHead>
                      <TableHead>Text</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-semibold">{run.count}x</TableCell>
                        <TableCell className="font-mono text-sm whitespace-nowrap">
                          {formatTimestamp(run.startTime)} - {formatTimestamp(run.endTime)}
                        </TableCell>
                        <TableCell className="text-sm max-w-md truncate" title={run.text}>
                          {run.text}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="longLines">
        {longLines.length === 0 ? (
          <EmptyTabMessage message={`No lines exceed ${VTT_LONG_LINE_THRESHOLD} characters.`} />
        ) : (
          <Card className="border-yellow-200 dark:border-yellow-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-600">
                <TextCursorInput className="size-5" />
                Long Lines (&gt;{VTT_LONG_LINE_THRESHOLD} chars)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {longLines.length} segment{longLines.length !== 1 ? 's' : ''} exceed the character threshold
                (avg: {avgChars} chars/segment)
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Chars</TableHead>
                      <TableHead className="w-28">Time</TableHead>
                      <TableHead>Text</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {longLines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-semibold">{line.charCount}</TableCell>
                        <TableCell className="font-mono text-sm">{formatTimestamp(line.start)}</TableCell>
                        <TableCell className="text-sm max-w-md truncate" title={line.text}>
                          {line.text}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="largeGaps">
        {result.largeGaps.length === 0 ? (
          <EmptyTabMessage message="No gaps larger than 30 seconds found." />
        ) : (
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                <XCircle className="size-5" />
                Large Gaps (&gt;30s) - Likely Missing Segments
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {result.largeGaps.length} gap{result.largeGaps.length !== 1 ? 's' : ''} detected
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <GapsTable gaps={result.largeGaps} />
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="significantGaps">
        {result.significantGaps.length === 0 ? (
          <EmptyTabMessage message="No gaps larger than 5 seconds found." />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="size-5" />
                Significant Gaps (&gt;5s)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {result.significantGaps.length} gap{result.significantGaps.length !== 1 ? 's' : ''} sorted chronologically
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <GapsTable gaps={[...result.significantGaps].sort((a, b) => a.afterEnd - b.afterEnd)} />
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="uncovered">
        {result.minutesWithNoCoverage.length === 0 ? (
          <EmptyTabMessage message="All minutes have caption coverage." />
        ) : (
          <Card className="border-yellow-200 dark:border-yellow-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-600">
                <AlertTriangle className="size-5" />
                Minutes with No Caption Coverage
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {result.minutesWithNoCoverage.length} minute{result.minutesWithNoCoverage.length !== 1 ? 's' : ''} have zero caption coverage
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {result.minutesWithNoCoverage.map((min) => (
                  <Badge key={min} variant="outline" className="font-mono">
                    {min}:00
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
}

function GapsTable({ gaps }: { gaps: VttGap[] }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Gap Duration</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {gaps.map((gap, idx) => (
            <TableRow key={idx}>
              <TableCell className="font-semibold">
                {formatDuration(gap.gapSeconds)}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {formatTimestamp(gap.afterEnd)}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {formatTimestamp(gap.beforeStart)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function HallucinationsTab({ hallucinations }: { hallucinations?: VttHallucinationAnalysis }) {
  const wordReps = hallucinations?.wordRepetitions ?? [];
  const duplicates = hallucinations?.duplicateTexts ?? [];
  const phrases = hallucinations?.hallucinationPhrases ?? [];
  const score = hallucinations?.score ?? 0;

  const totalIssues = wordReps.length + duplicates.length + phrases.length;

  if (totalIssues === 0) {
    return <EmptyTabMessage message="No hallucination patterns detected." />;
  }

  // Determine severity color based on score
  const severityColor = score >= 50 ? 'border-red-500' : score >= 25 ? 'border-yellow-500' : 'border-purple-500';
  const severityText = score >= 50 ? 'High' : score >= 25 ? 'Medium' : 'Low';
  const severityTextColor = score >= 50 ? 'text-red-600' : score >= 25 ? 'text-yellow-600' : 'text-purple-600';

  return (
    <div className="space-y-4">
      {/* Score Summary */}
      <Card className={severityColor}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bug className={`size-5 ${severityTextColor}`} />
            <span className={severityTextColor}>Hallucination Score: {score}/100</span>
            <Badge variant="outline" className={severityColor + ' ' + severityTextColor}>
              {severityText} Severity
            </Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Whisper transcription artifacts detected. These indicate audio segments where the model failed to transcribe correctly.
          </p>
        </CardHeader>
      </Card>

      {/* Word Repetitions */}
      {wordReps.length > 0 && (
        <Card className="border-red-200 dark:border-red-900">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <Repeat className="size-5" />
              Repeated Word Loops
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Segments where a single word repeats excessively (&gt;10 times) — classic Whisper failure mode
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Word</TableHead>
                    <TableHead className="w-20">Count</TableHead>
                    <TableHead className="w-28">Time</TableHead>
                    <TableHead>Text Preview</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wordReps.map((rep, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono font-semibold text-red-600">"{rep.word}"</TableCell>
                      <TableCell className="font-semibold">{rep.count}x</TableCell>
                      <TableCell className="font-mono text-sm">{formatTimestamp(rep.start)}</TableCell>
                      <TableCell className="text-sm max-w-md truncate text-muted-foreground" title={rep.text}>
                        {rep.text}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Duplicate Texts */}
      {duplicates.length > 0 && (
        <Card className="border-yellow-200 dark:border-yellow-900">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-600">
              <FileText className="size-5" />
              Non-Adjacent Duplicate Text
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Identical long text appearing at different times — may indicate hallucinated content
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Count</TableHead>
                    <TableHead>Timestamps</TableHead>
                    <TableHead>Text</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicates.map((dup, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-semibold">{dup.count}x</TableCell>
                      <TableCell className="font-mono text-sm max-w-[400px]">
                        {dup.timestamps.map(t => formatTimestamp(t)).join(', ')}
                      </TableCell>
                      <TableCell className="text-sm max-w-md truncate" title={dup.text}>
                        {dup.text}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Known Hallucination Phrases */}
      {phrases.length > 0 && (
        <Card className="border-purple-200 dark:border-purple-900">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-purple-600">
              <AlertTriangle className="size-5" />
              Known Whisper Hallucination Phrases
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Common phrases Whisper hallucinates during silence or unclear audio
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead className="w-28">Time</TableHead>
                    <TableHead>Phrase</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {phrases.map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Badge variant="outline" className="border-purple-500 text-purple-600 capitalize">
                          {p.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatTimestamp(p.start)}</TableCell>
                      <TableCell className="text-sm max-w-md truncate" title={p.phrase}>
                        {p.phrase}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function EmptyTabMessage({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-8">
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <CheckCircle2 className="size-5 text-green-500" />
          <span>{message}</span>
        </div>
      </CardContent>
    </Card>
  );
}
